import { AddLengthRule, LengthRules } from "../../rules/lengthRules";
import { Rule } from "../../rules/rule";
import { isRule, Rules2Type, RuleSet, RuleType } from "../../rules/ruleSet";
import { ruleContextToString, ruleToString } from "../../rules/serializeRule";

/** quick & dirty way to stringify rules in a more readable way that JSON */
export function ruleSetToString(rule: RuleSet): string {
  let str = '{\n';
  const names = Object.getOwnPropertyNames(rule);
  names.forEach(name => {
    str += name + ': ';
    if (name === 'lexicalSets' && rule.lexicalSets) {
      str += lexicalSetsToString(rule.lexicalSets);
    } else if (name === 'syllableBreaks' && rule.rules) {
      // currently hard-coded
      str += 'englishPhonotactics,\n';
    } else if (name === 'phonemeLengths' && rule.phonemeLengths) {
      str += phonemeLengthsToString(rule.phonemeLengths);
    } else if (name === 'rules' && rule.rules) {
      str += rulesToString(rule.rules);
    } else if (name === 'rules2' && rule.rules2) {
      str += multiRulesToString(rule.rules2);
    } else {
      str += JSON.stringify((rule as Record<string, unknown>)[name]) + ",\n";
    }
  });
  return str + '}';
}

function lexicalSetsToString(record: Record<string, string>): string {
  let str = '{\n  ';
  const names = Object.getOwnPropertyNames(record);
  names.forEach(name => {
    str += name + ": '" + record[name] + "', ";
    if (name === 'MOUTH' || name === 'NURSE' || name === 'FORCE' || name === 'BATH' || name === 'FOOT') {
      str += '\n  ';
    } else if (name === 'commA') {
      str += '\n';
    }
  });
  return str + '},\n';
}

/** doesn't know how to serialize all rules */
function ruleToStringCheat(rule: RuleType): string {
  let str = '';
  const shortForm = ruleToString(rule);
  if (shortForm !== undefined) {
    // e.g., a > b / V_#
    str += `  ${shortForm}\n`;
  } else if (isRule(rule)) {
    // verbose form for rules that can't be converted to the short form
    str += "  from: '" + rule.from + "', to: '" + rule.to + "'";
    if (rule.previous) {
      str += ", previous: " + JSON.stringify(rule.previous);
    }
    if (rule.next) {
      str += ", next: " + JSON.stringify(rule.next);
    }
    str += ',\n';
  }
  return str;
}
function rulesToString(rules: RuleType[]): string {
  let str = '[\n';
  for (const rule of rules) {
    const s = ruleToStringCheat(rule);
    str += s;
  };
  return str + '],\n';
}
function multiRulesToString(multirules: Rules2Type): string {
  if (multirules.length > 0 && !Array.isArray(multirules[0])) {
    return rulesToString(multirules as RuleType[]);
  }
  multirules = multirules as RuleType[][];
  let str = '[\n';
  let i = 0;
  for (const list of multirules) {
    if (i++ !== 0) {
      str += ' reset';
    }
    for (const rule of list) {
      const s = ruleToStringCheat(rule);
      str += s;
    }
  }
  return str + '],\n';
}

/** convert entire phonemeLengths object to a string */
function phonemeLengthsToString(phonemeLengths: LengthRules): string {
  let str = '{\n';
  const map = lengthMapToString(phonemeLengths.map);
  const adds = phonemeLengths.adds ? lengthAddsToString(phonemeLengths.adds) : '';
  return str + map + adds + '},\n';
}

function lengthMapToString(phonemeLengths: Record<string, number>): string {
  let str = '  map: {';
  const names = Object.getOwnPropertyNames(phonemeLengths);
  names.forEach((name, i) => {
    if (i % 7 === 0) {
      str += '\n    ';
    }
    str += name + ": " + phonemeLengths[name] + ", ";
  });
  return str + '\n  },\n';
}

function lengthAddsToString(adds: AddLengthRule[]): string {
  let str = '  adds: [';
  for (const add of adds) {
    const rule = addRuleToString(add);
    str += '\n    ' + rule;
  };

  return str + '\n  ],\n';
}

function addRuleToString(add: AddLengthRule): string {
  const fakeRule: Rule = { ...add, from: '', to: '' };
  const shortForm = ruleContextToString(fakeRule);
  if (shortForm !== undefined) {
    // e.g., -50 / V_#
    return `${add.addMs.toString()}${shortForm}`;
  }

  // verbose form for rules that can't be converted to the short form
  return JSON.stringify(add);
}
